/**
 * MonthlyContributionSheet
 *
 * Fija la aportación MENSUAL de un plan personal (B2C) partiendo de un importe ya puesto. Solo
 * lo abre el enlace del correo (`?action=increase_contribution&amount=50`, Arca-Team/product#3):
 * en el panel B2C la mensual se cambia con el slider y no había modal para ella. El slider y su
 * botón «Cambiar a X €/mes» no cambian.
 *
 * Misma estructura que `OneTimeContributionSheet`, sin rediseño ni proyección. Guarda quien lo
 * monta (`IndividualDashboardV2.handleConfigureContribution`, con su gate de IBAN): el
 * componente es nuevo, el guardado no.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { fmtNum } from '../../utils/formatArcaNumbers';

interface MonthlyContributionSheetProps {
	open: boolean;
	onClose: () => void;
	/** Importe con el que se abre: lo que ya aporta + lo que trae el correo. */
	initialAmount: number;
	/** Lo que aporta hoy al mes. No se puede bajar de ahí (igual que en el modal B2B). */
	currentAmount: number;
	/** Máximo mensual (el mismo que el slider). Sin él, no hay tope. */
	maxAmount?: number;
	onConfirm: (amount: number) => Promise<void> | void;
	translate: (key: string) => string;
}

const INTEGER_PATTERN = /^\d+$/;

const MonthlyContributionSheet: React.FC<MonthlyContributionSheetProps> = ({
	open,
	onClose,
	initialAmount,
	currentAmount,
	maxAmount,
	onConfirm,
	translate
}) => {
	const [amount, setAmount] = useState(String(initialAmount));
	const [loading, setLoading] = useState(false);
	// Si ya ha escrito algo, un `initialAmount` que cambia con el modal abierto (el tope del
	// autónomo llega tarde) no le pisa lo escrito. Al abrirse otra vez, se empieza de cero.
	const editedRef = useRef(false);

	useEffect(() => {
		if (!open) {
			editedRef.current = false;
			return;
		}
		if (!editedRef.current) setAmount(String(initialAmount));
	}, [open, initialAmount]);

	const minAmount = Math.max(currentAmount, 1);
	const numericAmount = Number(amount);
	const withAmount = (key: string, value: number) =>
		translate(key).replace('{{amount}}', fmtNum(value, 0));

	// El motivo por el que no se puede confirmar; `null` si el importe vale.
	const error = !INTEGER_PATTERN.test(amount)
		? translate('contributionPreset.errorNotInteger')
		: numericAmount < minAmount
			? currentAmount > 0
				? withAmount('contributionPreset.errorBelowCurrent', currentAmount)
				: withAmount('contributionPreset.errorBelowMin', minAmount)
			: maxAmount !== undefined && numericAmount > maxAmount
				? withAmount('contributionPreset.errorAboveMax', maxAmount)
				: null;
	const isValid = error === null;

	const handleConfirm = async () => {
		// El Enter del campo no pasa por el `disabled` del botón: se revalida aquí, y `loading`
		// evita dos guardados seguidos.
		if (!isValid || loading) return;
		setLoading(true);
		try {
			await onConfirm(numericAmount);
		} catch {
			// Quien guarda avisa del error y decide si se cierra; aquí solo se suelta el botón.
		} finally {
			setLoading(false);
		}
	};

	const title =
		currentAmount > 0
			? withAmount('contributionPreset.raiseTitle', initialAmount - currentAmount)
			: withAmount('contributionPreset.activateTitle', initialAmount);

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader className="pb-2">
					<DialogTitle className="text-xl">{title}</DialogTitle>
					<DialogDescription className="text-sm">
						{withAmount('contributionPreset.activateMessage', isValid ? numericAmount : 0)}
					</DialogDescription>
				</DialogHeader>

				<div className="py-3">
					<label
						htmlFor="monthly-contribution-amount"
						className="block text-sm font-medium text-foreground mb-1.5"
					>
						{translate('contributionPreset.amountLabel')}
					</label>
					<div className="relative">
						<input
							id="monthly-contribution-amount"
							type="number"
							inputMode="numeric"
							min={minAmount}
							max={maxAmount}
							step={1}
							value={amount}
							onChange={(e) => {
								editedRef.current = true;
								setAmount(e.target.value);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleConfirm();
							}}
							className="input-arca w-full text-base pr-8"
						/>
						<span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
							€
						</span>
					</div>
					{error && (
						<p
							role="status"
							aria-live="polite"
							className="text-xs text-destructive pt-2 leading-relaxed"
						>
							{error}
						</p>
					)}
				</div>

				<div className="flex gap-3 pt-2">
					<Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>
						{translate('contributionPreset.cancel')}
					</Button>
					<Button className="flex-1" onClick={handleConfirm} disabled={loading || !isValid}>
						{loading ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							translate('contributionPreset.confirm')
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default MonthlyContributionSheet;
